import React, { useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { useToast } from "../../../../../components/toast-container";
import { useValidateTimeFields } from "../../hooks/useValidateTimeFields";
import LocationPicker from "../../../../../components/customs/map/LocationPicker";
import InlineAlert from "../../../../../components/common/InlineAlert";
import { useAuthSession } from "../../../../../features/auth/shared/hooks/auth-session-context";
import { useGetCategoryOptions, useGetInterestOptions } from "../../../../common/public-options";
import type { EventDetailForAdminResponse } from "../../types/event.types";
import { mapEventToFormValues } from "../../utils/event.utils";
import {
  createDefaultEventEditorValues,
  createEventEditorSchema,
  updateEventEditorSchema,
  type EventEditorFormValues,
  type EventEditorSubmitValues,
} from "./event.scheme";
import { getBrowserTimeZone } from "../../../../../lib/date-time";
import {
  BasicInformationSection,
  EventDetailsSection,
  EventImageSection,
  LocationSection,
  RewardsAttendanceSection,
  ScheduleSection,
} from "./EventFormSections";

import "../Event.css";
import { getUserGeoJsonBoundary } from "../../../../../components/customs/map/fairburnBoundary";
import { getApiErrorMessage } from "../../../../../lib/result-codes";

export type { EventEditorFormValues, EventEditorSubmitValues } from "./event.scheme";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

type EventEditorProps = {
  mode: "create" | "edit";
  event?: EventDetailForAdminResponse | null;
  onSubmit?: (values: EventEditorSubmitValues) => Promise<void> | void;
  formId?: string;
  onStateChange?: (state: { isSubmitting: boolean }) => void;
};

const getTimezoneOptions = () => {
  const browserTimeZone = getBrowserTimeZone();
  const supportedTimeZones =
    typeof Intl !== "undefined" &&
    typeof (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf === "function"
      ? (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf(
          "timeZone",
        )
      : [];

  return Array.from(new Set([browserTimeZone, ...supportedTimeZones]))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ label: value, value }));
};

const ongoingEditableFields = new Set([
  "name",
  "description",
  "image",
  "categoryId",
  "subCategoryIds",
]);

export default function EventEditor({
  mode,
  event,
  onSubmit,
  formId,
  onStateChange,
}: EventEditorProps) {
  const { user } = useAuthSession();
  const userGeoJsonBoundary = getUserGeoJsonBoundary(user?.organization_id);
  const { categoryOptions, fetchCategoryOptions } = useGetCategoryOptions();
  const { interestOptions: subCategoryOptions, fetchInterestOptions: fetchSubCategoryOptions } =
    useGetInterestOptions();
  const { addToast } = useToast();
  const { validateTimeFields } = useValidateTimeFields();

  const isCreateMode = mode === "create";
  const formSchema = useMemo(
    () => (isCreateMode ? createEventEditorSchema : updateEventEditorSchema),
    [isCreateMode],
  );
  const form = useForm<EventEditorFormValues>({
    resolver: zodResolver(formSchema as any) as any,
    defaultValues: createDefaultEventEditorValues(),
    mode: "onSubmit",
  });
  const {
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    watch,
    formState: { isSubmitting },
  } = form;
  const [openLocationPicker, setOpenLocationPicker] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const viewEvent = event || null;
  const initialPoints = useMemo(
    () => (viewEvent && mode !== "create" ? mapEventToFormValues(viewEvent).loadedPoints : ""),
    [mode, viewEvent],
  );
  const locationValue = watch("location");
  const latValue = watch("lat");
  const lonValue = watch("lon");

  useEffect(() => onStateChange?.({ isSubmitting }), [isSubmitting, onStateChange]);

  const isOngoing = viewEvent?.event_status === "ongoing";
  const hasPassedEndTime = !!viewEvent?.end_time && new Date() > new Date(viewEvent.end_time);
  const isCompleted = viewEvent?.event_status === "completed" || hasPassedEndTime;
  const canEditField = (fieldName: string) => {
    if (isCompleted) return false;
    if (isOngoing) return ongoingEditableFields.has(fieldName);
    return true;
  };

  useEffect(() => {
    setFormError(null);
    fetchCategoryOptions();
    fetchSubCategoryOptions();
  }, [fetchCategoryOptions, fetchSubCategoryOptions]);

  useEffect(() => {
    setFormError(null);
    if (mode === "create") {
      reset(createDefaultEventEditorValues());
      return;
    }
    if (!viewEvent) return;
    const { values } = mapEventToFormValues(viewEvent);
    reset(values);
  }, [mode, reset, viewEvent]);

  const onValidSubmit = handleSubmit(async (values) => {
    setFormError(null);
    const timeError = validateTimeFields(values.start, values.end, values.eventDate, {
      minimumDurationMinutes: 0,
      validateEndNotPast: true,
    });
    if (timeError) {
      setFormError(timeError);
      return;
    }
    try {
      await onSubmit?.(values);
    } catch (submitError: unknown) {
      console.error("Failed to submit event form:", submitError);
      const errorMessage = getApiErrorMessage(submitError, "An error occurred");
      setFormError(errorMessage);
      addToast({ type: "error", title: "Error!", message: errorMessage });
    }
  });

  return (
    <FormProvider {...form}>
      <React.Fragment>
        <form
          id={formId}
          onSubmit={onValidSubmit}
          className="mx-auto grid w-full max-w-[1600px] gap-4 px-4 py-5 sm:px-6"
        >
          {formError && (
            <InlineAlert
              className="mb-0"
              message={formError}
            />
          )}
          <EventImageSection
            required={isCreateMode}
            isSubmitting={isSubmitting}
            canEditField={canEditField}
          />
          <BasicInformationSection
            categoryOptions={categoryOptions}
            subCategoryOptions={subCategoryOptions}
            isSubmitting={isSubmitting}
            canEditField={canEditField}
          />
          <EventDetailsSection
            isSubmitting={isSubmitting}
            canEditField={canEditField}
          />
          <LocationSection
            isSubmitting={isSubmitting}
            canEditField={canEditField}
            onOpenLocationPicker={() => setOpenLocationPicker(true)}
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <ScheduleSection
              timezoneOptions={timezoneOptions}
              isSubmitting={isSubmitting}
              canEditField={canEditField}
            />
            <RewardsAttendanceSection
              key={`${mode}-${viewEvent?.id ?? "new"}-${initialPoints}`}
              initialPoints={initialPoints}
              isSubmitting={isSubmitting}
              canEditField={canEditField}
            />
          </div>
        </form>
        <LocationPicker
          isOpen={openLocationPicker}
          onClose={() => setOpenLocationPicker(false)}
          geoJson={userGeoJsonBoundary}
          lat={Number(latValue) || undefined}
          lon={Number(lonValue) || undefined}
          address={locationValue}
          onChange={({ address, latitude, longitude }) => {
            setOpenLocationPicker(false);
            setValue("lat", String(latitude ?? ""), { shouldDirty: true, shouldValidate: true });
            setValue("lon", String(longitude ?? ""), { shouldDirty: true, shouldValidate: true });
            setValue("location", address || "", { shouldDirty: true, shouldValidate: true });
            clearErrors(["location", "lat", "lon"]);
          }}
          restrictToUSA={
            !["johnnguyen.it.39@gmail.com", "internal.support@civicpoints.io"].includes(
              user?.email || "",
            )
          }
        />
      </React.Fragment>
    </FormProvider>
  );
}
